import * as Papa from "papaparse";

/**
 * File parsing for the mapped import: CSV via papaparse, XLSX via a
 * hand-rolled ZIP + sheet-XML reader.
 *
 * The XLSX reader is written here rather than imported because adding a
 * spreadsheet dependency is a supply-chain decision this repo gates behind
 * human review (dependency policy), and the subset an import needs, one
 * sheet, strings and numbers, is small: unzip with the browser's own
 * DecompressionStream, then walk the sheet XML with DOMParser. The xlsx
 * EXPORT path (reports) is hand-rolled for the same reason.
 */

export interface ParsedTable {
  headers: string[];
  /** Row objects keyed by header, like papaparse's header:true output. */
  rows: Record<string, string>[];
}

export const parseCsvFile = (file: File): Promise<ParsedTable> =>
  new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) =>
        resolve({
          headers: results.meta.fields ?? [],
          rows: results.data,
        }),
      error: reject,
    });
  });

// ---------------------------------------------------------------------------
// XLSX
// ---------------------------------------------------------------------------

const EOCD_SIG = 0x06054b50;
const CDIR_SIG = 0x02014b50;

interface ZipEntry {
  name: string;
  compression: number;
  compressedSize: number;
  localHeaderOffset: number;
}

const readZipEntries = (buffer: ArrayBuffer): ZipEntry[] => {
  const view = new DataView(buffer);
  // EOCD is within the last 64KB + 22 bytes; scan backwards for the signature.
  let eocd = -1;
  const start = Math.max(0, buffer.byteLength - 65558);
  for (let i = buffer.byteLength - 22; i >= start; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("Not a valid .xlsx file (no ZIP directory)");

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];
  const decoder = new TextDecoder();

  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CDIR_SIG) break;
    const compression = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(
      new Uint8Array(buffer, offset + 46, nameLength),
    );
    entries.push({ name, compression, compressedSize, localHeaderOffset });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
};

const extractEntry = async (
  buffer: ArrayBuffer,
  entry: ZipEntry,
): Promise<string> => {
  const view = new DataView(buffer);
  const base = entry.localHeaderOffset;
  const nameLength = view.getUint16(base + 26, true);
  const extraLength = view.getUint16(base + 28, true);
  const dataStart = base + 30 + nameLength + extraLength;
  const compressed = new Uint8Array(buffer, dataStart, entry.compressedSize);

  if (entry.compression === 0) {
    return new TextDecoder().decode(compressed);
  }
  if (entry.compression !== 8) {
    throw new Error("Unsupported compression in .xlsx");
  }
  const stream = new Blob([compressed])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  return await new Response(stream).text();
};

/** "BC" -> 54 (zero-based column index). */
const columnIndex = (cellRef: string): number => {
  let index = 0;
  for (const char of cellRef) {
    if (char < "A" || char > "Z") break;
    index = index * 26 + (char.charCodeAt(0) - 64);
  }
  return index - 1;
};

export const parseXlsxFile = async (file: File): Promise<ParsedTable> => {
  const buffer = await file.arrayBuffer();
  const entries = readZipEntries(buffer);

  const find = (name: string) => entries.find((entry) => entry.name === name);
  const sheetEntry =
    find("xl/worksheets/sheet1.xml") ??
    entries.find((entry) => /^xl\/worksheets\/sheet\d+\.xml$/.test(entry.name));
  if (!sheetEntry) throw new Error("No worksheet found in the .xlsx file");

  const parser = new DOMParser();

  // Shared strings: most text cells point into this table.
  const shared: string[] = [];
  const sharedEntry = find("xl/sharedStrings.xml");
  if (sharedEntry) {
    const doc = parser.parseFromString(
      await extractEntry(buffer, sharedEntry),
      "application/xml",
    );
    for (const si of Array.from(doc.getElementsByTagName("si"))) {
      // A cell with formatting runs has several <t>; concatenate them.
      const parts = Array.from(si.getElementsByTagName("t")).map(
        (t) => t.textContent ?? "",
      );
      shared.push(parts.join(""));
    }
  }

  const sheetDoc = parser.parseFromString(
    await extractEntry(buffer, sheetEntry),
    "application/xml",
  );

  const grid: string[][] = [];
  for (const row of Array.from(sheetDoc.getElementsByTagName("row"))) {
    const cells: string[] = [];
    for (const cell of Array.from(row.getElementsByTagName("c"))) {
      const ref = cell.getAttribute("r") ?? "";
      const type = cell.getAttribute("t");
      let value = "";
      if (type === "inlineStr") {
        value = cell.textContent ?? "";
      } else {
        const v = cell.getElementsByTagName("v")[0];
        value = v?.textContent ?? "";
        if (type === "s") value = shared[Number(value)] ?? "";
      }
      const index = ref ? columnIndex(ref) : cells.length;
      cells[index] = value;
    }
    grid.push(cells);
  }

  if (grid.length === 0) return { headers: [], rows: [] };

  const headers = (grid[0] ?? []).map((h, i) =>
    (h || `Column ${i + 1}`).trim(),
  );
  const rows = grid
    .slice(1)
    .filter((cells) => cells.some((value) => (value ?? "").trim() !== ""))
    .map((cells) => {
      const row: Record<string, string> = {};
      headers.forEach((header, i) => {
        row[header] = (cells[i] ?? "").toString();
      });
      return row;
    });

  return { headers, rows };
};

export const parseTabularFile = (file: File): Promise<ParsedTable> =>
  /\.(xlsx|xlsm)$/i.test(file.name) ? parseXlsxFile(file) : parseCsvFile(file);

// ---------------------------------------------------------------------------
// Column-mapping guesses
// ---------------------------------------------------------------------------

/** Target fields the contact importer understands, with header aliases. */
export const CONTACT_IMPORT_TARGETS: {
  field: string;
  label: string;
  aliases: string[];
}[] = [
  {
    field: "first_name",
    label: "First name",
    aliases: ["firstname", "first", "given name", "prenom"],
  },
  {
    field: "last_name",
    label: "Last name",
    aliases: ["lastname", "last", "surname", "family name"],
  },
  {
    field: "email_work",
    label: "Email (work)",
    aliases: ["email", "e-mail", "email address", "work email"],
  },
  {
    field: "email_home",
    label: "Email (home)",
    aliases: ["home email", "personal email"],
  },
  {
    field: "phone_work",
    label: "Phone (work)",
    aliases: [
      "phone",
      "phone number",
      "mobile",
      "cell",
      "work phone",
      "telephone",
    ],
  },
  { field: "phone_home", label: "Phone (home)", aliases: ["home phone"] },
  {
    field: "company",
    label: "Company",
    aliases: [
      "company name",
      "organization",
      "organisation",
      "business",
      "account",
    ],
  },
  {
    field: "title",
    label: "Job title",
    aliases: ["job title", "position", "role"],
  },
  {
    field: "background",
    label: "Notes",
    aliases: ["notes", "note", "description", "background info"],
  },
  {
    field: "status",
    label: "Status",
    aliases: ["lifecycle", "stage", "lead status"],
  },
  { field: "tags", label: "Tags", aliases: ["tag", "labels", "groups"] },
  {
    field: "linkedin_url",
    label: "LinkedIn",
    aliases: ["linkedin", "linkedin profile"],
  },
];

const normalize = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Guess which source column feeds each target field. Exact normalized match
 * first, then alias match, first unclaimed column wins, so "Email" maps to
 * email_work and a second "Email 2" column stays unmapped for the human.
 */
export const guessMapping = (headers: string[]): Record<string, string> => {
  const mapping: Record<string, string> = {};
  const claimed = new Set<string>();

  for (const target of CONTACT_IMPORT_TARGETS) {
    const wanted = [target.field, target.label, ...target.aliases].map(
      normalize,
    );
    const match = headers.find(
      (header) => !claimed.has(header) && wanted.includes(normalize(header)),
    );
    if (match) {
      mapping[target.field] = match;
      claimed.add(match);
    }
  }
  return mapping;
};

/** Apply a mapping, producing rows in the importer's schema. */
export const applyMapping = (
  rows: Record<string, string>[],
  mapping: Record<string, string>,
): Record<string, string>[] =>
  rows.map((row) => {
    const out: Record<string, string> = {};
    for (const [field, header] of Object.entries(mapping)) {
      if (!header) continue;
      const value = row[header];
      if (value != null && String(value).trim() !== "") {
        out[field] = String(value).trim();
      }
    }
    return out;
  });
